import { FC, memo, useCallback, useMemo, ReactNode } from "react";
import { useDispatch } from "react-redux";
import { ColumnInstance, Filters } from "react-table";
import { StagingBuilderStatuses } from "../../constants";
import { StagingBuilderMemo } from "../ProposalContainerColumnContainer";
import { fetchStagingBuilders } from "../../../redux/stagingBuilder/stagingBuilderActions";

interface Props {
  headers: ColumnInstance<StagingBuilderMemo>[];
  setFilter: (columnId: string, updater: any) => void;
  filters: Filters<StagingBuilderMemo>;
  children: () => ReactNode;
}

export const SearchHeader: FC<Props> = ({
  children,
  headers,
  setFilter,
  filters,
}) => {
  const dispatch = useDispatch();

  const statusHeader = headers.find(
    (col) => col.Header === "Status"
  ) as ColumnInstance<StagingBuilderMemo>;

  if (process.env.NODE_ENV === "development")
    if (statusHeader === undefined)
      throw new Error("statusHeader is undefined");

  const clearFilters = () => {
    headers.forEach((col) => {
      col.setFilter(undefined);
    });
  };
  const setStatusFilter = (value: StagingBuilderStatuses[] | undefined) =>
    setFilter("stagingStatus", (currentFilters: any) => {
      return value;
    });

  const setStagerFilter = (value: StagingBuilderStatuses[] | undefined) =>
    setFilter("stagerName", (currentFilters: any) => {
      return value;
    });
  const setClientFilter = (value: StagingBuilderStatuses[] | undefined) =>
    setFilter("clientName", (currentFilters: any) => {
      return value;
    });

  const currentStatusFilter: StagingBuilderStatuses[] = useMemo(
    () => filters.find((filter) => filter.id === "stagingStatus")?.value || [],
    [filters]
  );

  const currentStagerFilter: StagingBuilderStatuses[] =
    filters.find((filter) => filter.id === "stagerName")?.value || [];

  const currentClientFilter: StagingBuilderStatuses[] =
    filters.find((filter) => filter.id === "clientName")?.value || [];

  //   const currentStatusFilter: StagingBuilderStatuses[] =
  //     statusHeader.filterValue || [];

  let showProposals = 1 as 0 | 1 | 2,
    showHomeStagings = 1 as 0 | 1 | 2;
  // showRequests = 1 as 0 | 1 | 2,

  const isShown = useCallback(
    (
      statusToCheck: StagingBuilderStatuses | StagingBuilderStatuses[]
    ): -1 | 0 | 1 | 2 => {
      if (typeof statusToCheck !== "object")
        return Number(currentStatusFilter.includes(statusToCheck)) as 0 | 1;
      // if no filter this is true
      if (!currentStatusFilter.length) return -1;
      // an array of length 1 was given
      if (statusToCheck.length === 1)
        return Number(currentStatusFilter.includes(statusToCheck[0])) as 0 | 1;
      // an array of length > 1 was given
      return Number(
        statusToCheck.some((x) => currentStatusFilter.includes(x))
      ) as 0 | 1;
    },
    [currentStatusFilter]
  );

  const StagingBuilderStatusesLength = useMemo(
    () => Object.keys(StagingBuilderStatuses).length,
    []
  );

  // Helper function to convert status filters to API filter format
  const convertToAPIFilters = useCallback((statusFilter: StagingBuilderStatuses[]) => {
    const closedStatuses = [
      StagingBuilderStatuses.CLOSED,
      StagingBuilderStatuses.DEAL_FELL_THROUGH,
      StagingBuilderStatuses.PROPOSAL_FELL_THROUGH,
    ];
    const stagedStatuses = [StagingBuilderStatuses.STAGED];
    const requestStatuses = [
      StagingBuilderStatuses.PICKUP_REQUEST,
      StagingBuilderStatuses.LIMBO_REQUEST,
      StagingBuilderStatuses.PROPOSAL_HOLD_REQUEST,
      StagingBuilderStatuses.DEAL_FELL_THROUGH_REQUEST,
      StagingBuilderStatuses.PROPOSAL_APPROVAL_REQUEST,
      StagingBuilderStatuses.PROPOSAL_APPROVAL_REQUEST_HOLD,
    ];

    const hasAllClosed = closedStatuses.every((s) => statusFilter.includes(s));
    const hasNoClosed = closedStatuses.every((s) => !statusFilter.includes(s));
    const hasAllStaged = stagedStatuses.every((s) => statusFilter.includes(s));
    const hasNoStaged = stagedStatuses.every((s) => !statusFilter.includes(s));
    const hasAllRequests = requestStatuses.every((s) => statusFilter.includes(s));
    const hasNoRequests = requestStatuses.every((s) => !statusFilter.includes(s));

    return {
      showClosed: (hasNoClosed ? 0 : hasAllClosed ? 1 : 1) as 0 | 1 | 2,
      showStaged: (hasNoStaged ? 0 : hasAllStaged ? 1 : 1) as 0 | 1 | 2,
      showRequests: (hasNoRequests ? 0 : hasAllRequests ? 1 : 1) as 0 | 1 | 2,
    };
  }, []);

  // Dispatch fetch with filters whenever status filter changes
  const dispatchFetchWithFilters = useCallback((statusFilter: StagingBuilderStatuses[] | undefined) => {
    if (!statusFilter) {
      // No filter means show all
      dispatch(fetchStagingBuilders() as any);
    } else {
      const apiFilters = convertToAPIFilters(statusFilter);
      dispatch(fetchStagingBuilders(apiFilters) as any);
    }
  }, [dispatch, convertToAPIFilters]);

  return (
    <>
      <div className="mb-5">
        <div className="flex space-between flex-row-wrap">
          <PillButton
            type="STATUS"
            show={isShown([
              StagingBuilderStatuses.CLOSED,
              StagingBuilderStatuses.DEAL_FELL_THROUGH,
              StagingBuilderStatuses.PROPOSAL_FELL_THROUGH,
            ])}
            // show={isShown([StagingBuilderStatuses.CLOSED])}
            text={[`Show "Closed" SB's?`, `Hide "Closed" SB's?`]}
            setShow={setStatusFilter}
            currentFilter={currentStatusFilter}
            filterValue={[
              StagingBuilderStatuses.CLOSED,
              StagingBuilderStatuses.DEAL_FELL_THROUGH,
              StagingBuilderStatuses.PROPOSAL_FELL_THROUGH,
            ]}
            onFilterChange={dispatchFetchWithFilters}
          />
          <PillButton
            type="STATUS"
            show={isShown([StagingBuilderStatuses.STAGED])}
            text={[`Show Staged SB's?`, `Hide Staged SB's?`]}
            setShow={setStatusFilter}
            currentFilter={currentStatusFilter}
            filterValue={[StagingBuilderStatuses.STAGED]}
            onFilterChange={dispatchFetchWithFilters}
          />
          <PillButton
            type="STATUS"
            text={[`Show Upcoming SD's?`, `Hide Upcoming SD's?`]}
            setShow={setStatusFilter}
            currentFilter={currentStatusFilter}
            show={isShown([
              StagingBuilderStatuses.PICKUP_REQUEST,
              StagingBuilderStatuses.PICKUP_SCHEDULED,
            ])}
            filterValue={[
              StagingBuilderStatuses.PICKUP_REQUEST,
              StagingBuilderStatuses.PICKUP_SCHEDULED,
            ]}
            onFilterChange={dispatchFetchWithFilters}
          />
          <PillButton
            type="STATUS"
            show={isShown([])}
            text={[`Hide Requests?`, `Show Requests?`]}
            setShow={setStatusFilter}
            currentFilter={currentStatusFilter}
            filterValue={[StagingBuilderStatuses.CLOSED]}
            onFilterChange={dispatchFetchWithFilters}
          />
          <PillButton
            type="CALLBACK"
            show={0}
            text={[`Use Default Filters`]}
            setShow={() => {
              clearFilters();
              const defaultFilters = [
                1, 2, 3, 4, 5, 7, 8, 9, 10, 11, 13, 14, 15, 16, 18, 19,
              ];
              setStatusFilter(defaultFilters);
              dispatchFetchWithFilters(defaultFilters as StagingBuilderStatuses[]);
            }}
            currentFilter={currentStatusFilter}
            filterValue={[
              1, 2, 3, 4, 5, 7, 8, 9, 10, 11, 13, 14, 15, 16, 18, 19,
            ]}
          />
          {showProposals === 0 && (
            <PillButton
              type="STATUS"
              text={['Clear "Hide Proposals"', 'Clear "Hide Proposals"']}
              setShow={setStatusFilter}
              currentFilter={currentStatusFilter}
              filterValue={[StagingBuilderStatuses.CLOSED]}
              show={isShown([])}
            />
          )}
          {showHomeStagings === 0 && (
            <PillButton
              type="FILE_NUMBER"
              show={showHomeStagings}
              text={['Clear "Hide Home Staging"', 'Clear "Hide Home Staging"']}
              setShow={setStatusFilter}
              currentFilter={currentStatusFilter}
              filterValue={[StagingBuilderStatuses.CLOSED]}
            />
          )}
          {currentStagerFilter.length !== 0 && (
            <PillButton
              type="CLEAR_FILTER"
              show={0}
              text={[`Clear Stager Filter`]}
              setShow={setStagerFilter}
              currentFilter={currentStatusFilter}
              filterValue={undefined as unknown as string}
              onFilterChange={dispatchFetchWithFilters}
            />
          )}
          {currentClientFilter.length !== 0 && (
            <PillButton
              type="CLEAR_FILTER"
              show={0}
              text={[`Clear Stager Filter`]}
              setShow={setClientFilter}
              currentFilter={currentStatusFilter}
              filterValue={undefined as unknown as string}
              onFilterChange={dispatchFetchWithFilters}
            />
          )}
          {currentStatusFilter.length !== 0 &&
            currentStatusFilter.length !== StagingBuilderStatusesLength && (
              <PillButton
                type="CLEAR_ALL"
                show={0}
                text={[`Clear All Filters`]}
                setShow={clearFilters}
                currentFilter={currentStatusFilter}
                filterValue={undefined as unknown as StagingBuilderStatuses[]}
                onFilterChange={dispatchFetchWithFilters}
              />
            )}
        </div>
      </div>
      {children()}
    </>
  );
};

interface PillProps {
  show: -1 | 0 | 1 | 2;
  setShow: (val: StagingBuilderStatuses[] | undefined) => void;
  text: string[];
  disabled?: boolean;
  filterValue: StagingBuilderStatuses[] | string;
  currentFilter?: StagingBuilderStatuses[];
  type?: "CALLBACK" | "STATUS" | "FILE_NUMBER" | "CLEAR_ALL" | "CLEAR_FILTER";
  onFilterChange?: (statusFilter: StagingBuilderStatuses[] | undefined) => void;
}

const PillButton = memo(function ({
  show,
  setShow,
  text,
  disabled = false,
  filterValue,
  currentFilter,
  type,
  onFilterChange,
}: PillProps) {
  const width = useMemo(
    () => text.reduce((acc, cur) => (cur.length > acc ? cur.length : acc), 0),
    [text]
  );

  const colorClass = useMemo(
    () => ["bg-logo-blue", "bg-logo-purple text-white", "bg-logo-blue"],
    []
  );

  const handleFileNumberClick = () => {};

  const handleClick = () => {
    let newFilter: StagingBuilderStatuses[] | undefined;

    if (type === "FILE_NUMBER") return handleFileNumberClick();
    if (type === "CLEAR_ALL") {
      setShow(undefined);
      onFilterChange && onFilterChange(undefined);
      return;
    }
    if (type === "CLEAR_FILTER") {
      setShow(undefined);
      onFilterChange && onFilterChange(undefined);
      return;
    }

    if (typeof filterValue === "string") return;

    if (show === -1) {
      // no filter case
      newFilter = (
        Object.values(StagingBuilderStatuses).filter((val) =>
          Number(val)
        ) as StagingBuilderStatuses[]
      ).reduce(
        (acc, cur) => (filterValue.includes(cur) ? [...acc] : [...acc, cur]),
        [] as StagingBuilderStatuses[]
      );
      setShow(newFilter);
    } else if (show === 1) {
      newFilter = currentFilter?.reduce(
        (acc, cur) =>
          filterValue.includes(cur) ? [...acc] : [...acc, cur],
        [] as StagingBuilderStatuses[]
      );
      currentFilter && setShow(newFilter!);
    } else if (show === 2) {
      newFilter = undefined;
      setShow(undefined);
    } else {
      newFilter = currentFilter ? [...currentFilter, ...filterValue] : filterValue;
      currentFilter && setShow(newFilter);
    }

    // Dispatch fetch with new filters
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };
  // handling of no filter case
  const textIndex = show === -1 ? 1 : show;
  return (
    <button
      disabled={disabled}
      type="button"
      style={{ width: `${width + 1}ch` }}
      onClick={handleClick}
      className={`m-1 text-sm md:text-base btn btn-pill-checkbox ${
        colorClass[textIndex]
      } ${show === 1 ? "active" : ""}`}
    >
      {text[textIndex]}
      <input type="hidden" value={show} />
    </button>
  );
});
